import { treaty } from "@elysiajs/eden";
import type { App } from "@storyline/server";
import { env } from "@/src/common/constants/env";
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from "@/src/common/services/token-store.service";

const createHttpClient = () =>
	treaty<App>(env.SERVER_URL, {
		// Keep ISO date strings as strings so responses match the shared Zod contracts.
		parseDate: false,
		headers: async () => {
			const accessToken = await getAccessToken();
			const headers: Record<string, string> = {};
			if (accessToken) {
				headers.authorization = `Bearer ${accessToken}`;
			}
			return headers;
		},
	});

export type HttpClient = ReturnType<typeof createHttpClient>;

let impl: HttpClient = createHttpClient();

export const httpClient: HttpClient = new Proxy(impl, {
	get: (_target, property) => Reflect.get(impl, property),
});

export const __setHttpClientImpl = (next: HttpClient): void => {
	impl = next;
};

export const __resetHttpClientImpl = (): void => {
	impl = createHttpClient();
};

type WithError = { error: unknown };

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const isUnauthorized = (result: WithError): boolean => isRecord(result.error) && result.error.status === 401;

const redirectToLogin = (): void => {
	if (typeof window !== "undefined" && window.location.pathname !== "/login") {
		window.location.assign("/login");
	}
};

let refreshPromise: Promise<boolean> | null = null;

const performRefresh = async (): Promise<boolean> => {
	const refreshToken = await getRefreshToken();
	if (!refreshToken) {
		return false;
	}
	const { data, error } = await httpClient.auth.refresh.post({ refreshToken });
	if (error || !data) {
		return false;
	}
	await setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
	return true;
};

export const tryRefreshSession = (): Promise<boolean> => {
	if (!refreshPromise) {
		refreshPromise = performRefresh().finally(() => {
			refreshPromise = null;
		});
	}
	return refreshPromise;
};

export const handleSessionExpired = async (): Promise<void> => {
	await clearTokens();
	redirectToLogin();
};

export const request = async <T extends WithError>(call: () => Promise<T>): Promise<T> => {
	const first = await call();
	if (!isUnauthorized(first)) {
		return first;
	}
	if (!(await tryRefreshSession())) {
		await handleSessionExpired();
		return first;
	}
	const second = await call();
	if (isUnauthorized(second)) {
		await handleSessionExpired();
	}
	return second;
};
